// web/src/components/Turnstile.tsx
//
// Cloudflare Turnstile widget — A10-web of docs/ABUSE-PREVENTION.md.
//
// Usage:
//   <Turnstile sitekey={SITEKEY} onVerify={(token) => setToken(token)} />
//
// When SITEKEY is undefined / empty, the widget is a no-op (renders
// nothing) and onVerify never fires. This is the pre-launch path:
// users sign up without a captcha, the backend's TURNSTILE_SECRET is
// likewise empty, so server-side verification passes through.
//
// When SITEKEY is a Cloudflare test key (1x00000000000000000000AA or
// similar), the widget always passes — used for local dev and the
// staging environment.
//
// When SITEKEY is real, the widget challenges the user invisibly +
// fires onVerify with the resulting token. The signup form sends the
// token as `turnstileToken` in the POST body; the backend siteverifies.
//
// Sitekey is read from VITE_TURNSTILE_SITEKEY at build time — Vite
// substitutes the value into the bundle, so changing keys means a
// rebuild + redeploy (same model as STRIPE_PUBLISHABLE_KEY).
//
// We load the Cloudflare script on first mount and only once per page
// — subsequent <Turnstile> renders re-use the already-loaded global.

import { useEffect, useRef, type ReactElement } from 'react';

const SCRIPT_URL = 'https://challenges.cloudflare.com/turnstile/v0/api.js';
const SCRIPT_ID = 'mfj-turnstile-script';

interface TurnstileGlobal {
  render: (el: HTMLElement, opts: {
    sitekey: string;
    callback: (token: string) => void;
    'error-callback'?: () => void;
    'expired-callback'?: () => void;
    theme?: 'light' | 'dark' | 'auto';
    size?: 'normal' | 'compact';
  }) => string;
  reset: (widgetId: string) => void;
}

declare global {
  interface Window {
    turnstile?: TurnstileGlobal;
  }
}

export interface TurnstileProps {
  /** Cloudflare site key. Empty/undefined → widget renders nothing. */
  sitekey?: string;
  /** Called with the token on successful challenge completion. */
  onVerify: (token: string) => void;
  /** Optional — called when the token expires (user idle on the form). */
  onExpire?: () => void;
  /** Optional — called when verification fails (network, blocked, etc). */
  onError?: () => void;
}

/**
 * Mounts a Turnstile widget. Pass an onVerify callback to receive the
 * token; persist it in your form state and post it as `turnstileToken`
 * in the signup body.
 */
export function Turnstile({ sitekey, onVerify, onExpire, onError }: TurnstileProps): ReactElement | null {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const widgetIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (!sitekey) return; // unconfigured — render nothing
    const container = containerRef.current;
    if (!container) return;

    // Load the script once per page.
    if (!document.getElementById(SCRIPT_ID)) {
      const s = document.createElement('script');
      s.id = SCRIPT_ID;
      s.src = SCRIPT_URL;
      s.async = true;
      s.defer = true;
      document.head.appendChild(s);
    }

    let cancelled = false;

    // Poll for the global until it's available — it's set by the script
    // we just appended. Polling rather than onload because in dev (React
    // strict-mode double mount) we don't want to attach two listeners.
    const tick = () => {
      if (cancelled) return;
      const t = window.turnstile;
      if (!t) {
        setTimeout(tick, 50);
        return;
      }
      // Already rendered (StrictMode double-mount). Skip.
      if (widgetIdRef.current) return;
      widgetIdRef.current = t.render(container, {
        sitekey,
        callback: onVerify,
        'expired-callback': onExpire,
        'error-callback': onError,
        theme: 'auto',
      });
    };
    tick();

    return () => {
      cancelled = true;
      // Intentionally don't t.remove(widgetId) — Cloudflare's widget
      // handles its own cleanup, and remove() throws if the script
      // hasn't finished loading. Component unmount removes the
      // containing div, which detaches the iframe.
    };
  }, [sitekey, onVerify, onExpire, onError]);

  if (!sitekey) return null;
  return <div ref={containerRef} style={{ display: 'flex', justifyContent: 'center', margin: '12px 0' }} />;
}
